<template>
  <div>
    <!-- Split button with preset controls -->
    <div
      v-if="canCreatePresets"
      class="split-btn"
    >
      <button
        v-if="canCreatePresets"
        :class="btnClassName"
        data-test="graphics-save-button"
        @click="onClickSave"
      >
        Save
      </button>

      <div class="dropdown">
        <button
          data-toggle="dropdown"
          data-test="graphics-save-dropdown"
          :class="btnClassName"
        >
          <span class="fas fa-chevron-down" />
        </button>

        <div class="dropdown-menu dropdown-menu-right">
          <button
            v-if="isEditingTemplate"
            class="dropdown-menu-item"
            data-test="savetemplate-button-save"
            @click="onClickSavePreset"
          >
            Save Template
          </button>

          <button
            class="dropdown-menu-item"
            data-test="savetemplate-button-saveasnew"
            @click="onClickSaveNewPreset"
          >
            Save As {{ isEditingTemplate ? 'New' : '' }} Template
          </button>
        </div>
      </div>
    </div>

    <!-- Plain ole save button -->
    <button
      v-else
      :class="btnClassName"
      :disabled="!hasUnsavedChanges || autoSaveEnabled"
      data-test="graphics-save-button"
      @click="onClickSave"
    >
      Save
    </button>

    <confirm
      v-if="showConfirm == 'new'"
      body="Save as new template?"
      data-test="graphics-confirm-new"
      @confirm="onClickSaveNewPreset($event, true)"
      @close="showConfirm = false"
    />

    <confirm
      v-else-if="showConfirm == 'save'"
      body="Overwrite existing template?"
      data-test="graphics-confirm-save"
      @confirm="onClickSavePreset($event, true)"
      @close="showConfirm = false"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { useFormContext } from 'vee-validate';

import Packs from '../services/packs';
import toast from '../utils/toasts';
import Confirm from './Confirm.vue';

export default {
  components: {
    Confirm,
  },

  props: {
    hasUnsavedChanges: {
      type: Boolean,
      default: false,
    },
    autoSaveEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save'],
  setup() {
    const { validate } = useFormContext();
    return { validateForm: validate };
  },

  data() {
    return {
      showConfirm: false,
    };
  },

  computed: {
    ...mapState({
      templateId: (state) => state.graphicsTemplate.template_id,
      user: (state) => state.user.user,
    }),

    btnClassName() {
      return 'btn btn-style-a btn-sm save-button';
    },

    canCreatePresets() {
      return this.user.groups?.includes('design');
    },

    isEditingTemplate() {
      return !!this.templateId;
    },
  },

  mounted() {
    this.$ld.on(this.onChangeLDFlags);
  },

  beforeUnmount() {
    this.$ld.off(this.onChangeLDFlags);
  },

  methods: {
    ...mapActions({
      setTemplateTemplateId: 'graphicsTemplate/setTemplateTemplateId',
    }),

    onChangeLDFlags() {
      // Kept for other flags if needed
    },

    /**
    * Edit existing template.
    * Pulls current graphic template and will overwrite the existing template.
    */
    async onClickSavePreset(e, confirmed = false) {
      if (!confirmed) {
        this.showConfirm = 'save';
        return;
      }

      this.showConfirm = false;
      await this.saveExistingTemplate();
    },

    async saveExistingTemplate() {
      // Grab current template...
      const graphicTemplate = await this.$store.dispatch('graphicsTemplate/transformGraphicsTemplateStateForSave');

      if (!graphicTemplate) {
        // eslint-disable-next-line no-console
        console.error('Can not update template with no data');
        return;
      }

      // Transform graphic template to Template API format
      const templateData = this.transformToTemplateFormat(graphicTemplate);

      try {
        await Packs.updateTemplate(this.templateId, templateData);
        toast.makeToast({ text: 'Template successfully updated!' });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to update template:', err);
        toast.makeToast({ text: 'Failed to update template', type: 'error' });
      }
    },

    /**
    * Create a new preset, then store the preset ID for editing it further.
    * Pulls current graphic template and saves it as a preset.
    */
    async onClickSaveNewPreset(e, confirmed = false) {
      if (!confirmed) {
        this.showConfirm = 'new';
        return;
      }

      this.showConfirm = false;
      await this.saveNewTemplate();
    },

    async saveNewTemplate() {
      // Grab current template...
      const graphicTemplate = await this.$store.dispatch('graphicsTemplate/transformGraphicsTemplateStateForSave');

      if (!graphicTemplate) {
        // eslint-disable-next-line no-console
        console.error('Can not create template with no data');
        return;
      }

      // Transform graphic template to Template API format
      const templateData = this.transformToTemplateFormat(graphicTemplate);

      try {
        const result = await Packs.createTemplate(templateData);

        // Set template_id on current template
        this.setTemplateTemplateId({ value: result.id });

        toast.makeToast({ text: 'New template successfully created!' });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to create template:', err);
        toast.makeToast({ text: 'Failed to create template', type: 'error' });
      }
    },

    transformToTemplateFormat(graphicTemplate) {
      // Extract metadata from tags or use defaults
      const tags = graphicTemplate.tags || [];

      // Deep clone elements to avoid mutating original
      const elements = JSON.parse(JSON.stringify(graphicTemplate.elements || []));

      // Strip PDK partner information from elements
      const cleanedElements = this.stripPartnerData(elements);

      return {
        title: graphicTemplate.title || 'Untitled',
        description: '',
        thumbnailUrl: graphicTemplate.preview_url || null,
        assetType: 'graphic',
        width: graphicTemplate.width || null,
        height: graphicTemplate.height || null,
        verticals: [],
        storyTypes: [],
        theme: tags.includes('beta') ? 'beta' : null,
        dataTypes: [],
        layout: null,
        signals: tags,
        settings: {
          // Include other relevant settings from the graphic template
          ...graphicTemplate,
          elements: cleanedElements,
        },
      };
    },

    /**
     * Recursively strip partner data from elements and their children.
     * Removes pregenerated URLs and experience IDs so users go through
     * the connection flow when using the template.
     */
    stripPartnerData(elements) {
      if (!Array.isArray(elements)) {
        return elements;
      }

      return elements.map((element) => {
        const cleanedElement = { ...element };

        // Only process elements that have a partner property
        if (cleanedElement.partner) {
          // Remove partner-specific pregenerated data
          delete cleanedElement.url;
          delete cleanedElement.permalink;
          delete cleanedElement.trackingLinkRid;
          delete cleanedElement.experienceId;
          delete cleanedElement.imageUrl;
        }

        // Recursively clean child elements if they exist
        if (cleanedElement.elements && Array.isArray(cleanedElement.elements)) {
          cleanedElement.elements = this.stripPartnerData(cleanedElement.elements);
        }

        return cleanedElement;
      });
    },

    async onClickSave() {
      const { valid, errors } = await this.validateForm();

      if (!valid) {
        this.reportInvalidForm(errors);
        return;
      }

      this.$emit('save');
    },

    /**
     * Validation errors in the editor are easy to miss (they can live in a
     * collapsed settings section, or on fields for an element that is no
     * longer visible), which makes the Save button look unresponsive. Report
     * them as a warning so we can find the fields that block saving.
     */
    reportInvalidForm(errors) {
      const fieldErrors = errors || {};
      const fields = Object.keys(fieldErrors);
      const fieldList = fields.length ? fields.join(', ') : 'unknown';

      const error = new Error(`Template save blocked by invalid form fields: ${fieldList}`);
      error.name = 'InvalidTemplateFormError';

      window.Sentry?.captureException(error, {
        level: 'warning',
        tags: {
          'graphics.form': 'save-template',
          'graphics.invalid_field_count': fields.length,
        },
        extra: {
          fields,
          errors: fieldErrors,
          templateId: this.templateId,
          isEditingTemplate: this.isEditingTemplate,
          selectedElementCount: this.$store.state.templates?.selectedElements?.length || 0,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.save-button {
  padding: .7rem 2rem;
  border-radius: 6px;
  border: none;
}
</style>
